import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  PortalDocumentResult,
  PortalTaxPayerBranding,
  getPortalTaxPayer,
  searchPortalDocuments,
} from "../../api/public-portal";
import LookupForm from "./LookupForm";
import ResultsList from "./ResultsList";
import PortalFooter from "./PortalFooter";

const PublicPortalPage: React.FC = () => {
  const { uniqueId } = useParams<{ uniqueId: string }>();
  const [branding, setBranding] = useState<PortalTaxPayerBranding | null>(null);
  const [brandingLoading, setBrandingLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [results, setResults] = useState<PortalDocumentResult[] | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [warningMsg, setWarningMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBrandingLoading(true);
      const data = await getPortalTaxPayer(uniqueId);
      if (cancelled) return;
      if (!data) {
        setNotFound(true);
      } else {
        setBranding(data);
      }
      setBrandingLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [uniqueId]);

  const handleSubmit = async (
    ruc: string,
    documentNumber: string,
    turnstileToken?: string,
  ) => {
    setLoading(true);
    setErrorMsg(null);
    setWarningMsg(null);
    setResults(null);
    const resp = await searchPortalDocuments(
      uniqueId,
      ruc,
      documentNumber,
      turnstileToken,
    );
    if (resp.code) {
      setErrorMsg(resp.message ?? "Ocurrió un error.");
    } else {
      setResults(resp.results ?? []);
      if (resp.warning) {
        setWarningMsg(
          "Verifique cuidadosamente los datos antes de volver a intentar. Múltiples búsquedas sin resultados pueden suspender temporalmente el acceso por motivos de seguridad.",
        );
      }
    }
    setLoading(false);
  };

  if (brandingLoading) {
    return (
      <div style={pageContainer()}>
        <div style={{ padding: 32, textAlign: "center" }}>Cargando...</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={pageContainer()}>
        <div style={{ padding: 32, textAlign: "center", color: "var(--theme-muted)" }}>
          Portal no disponible.
        </div>
        <PortalFooter />
      </div>
    );
  }

  return (
    <div style={pageContainer()}>
      <div style={contentStyle()}>
        <header
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            marginBottom: 24,
          }}
        >
          {branding?.logoUrl && (
            <img
              src={branding.logoUrl}
              alt={branding.socialName}
              style={{ height: 48, objectFit: "contain" }}
            />
          )}
          <div>
            <h1 style={{ margin: 0, fontSize: "1.4rem" }}>
              {branding?.fantasyName || branding?.socialName}
            </h1>
            <div style={{ color: "var(--theme-muted)", fontSize: "0.9rem" }}>
              Consulta de Documentos Electrónicos
            </div>
          </div>
        </header>

        <LookupForm loading={loading} onSubmit={handleSubmit} />

        {errorMsg && (
          <div
            style={{
              marginTop: 16,
              padding: 12,
              background: "var(--theme-error-surface)",
              color: "var(--theme-error-text)",
              borderRadius: 4,
              borderLeft: "4px solid var(--theme-error-text)",
            }}
          >
            {errorMsg}
          </div>
        )}

        {warningMsg && (
          <div
            style={{
              marginTop: 16,
              padding: 12,
              background: "var(--theme-warning-surface)",
              color: "var(--theme-warning-text)",
              borderRadius: 4,
              borderLeft: "4px solid #d4a017",
              fontSize: "0.9rem",
            }}
          >
            <strong>Aviso de seguridad: </strong>
            {warningMsg}
          </div>
        )}

        {results !== null && (
          <div style={{ marginTop: 24 }}>
            <ResultsList results={results} />
          </div>
        )}
      </div>
      <PortalFooter />
    </div>
  );
};

const pageContainer = (): React.CSSProperties => ({
  display: "flex",
  flexDirection: "column",
  background: "transparent",
});

const contentStyle = (): React.CSSProperties => ({
  flex: 1,
  width: "100%",
  margin: 0,
  padding: 16,
  background: "var(--theme-surface)",
});

export default PublicPortalPage;
